import MOCK_LEPROSY_ANNOTATIONS from './mock-leprosy-annotations.json';
import {
  clampConfidence,
  confidenceThresholdForTerm,
  visibilityByTerm,
  visibilityForTerm
} from './term-visualization';

export const LEPROSY_MODEL_TYPE = 'leprosy';

const DEFAULT_AFB_CONFIDENCE_THRESHOLD = 0;
const LEPROSY_DETECTION_TYPES = ['afb'];
const LEPROSY_AFB_TERMS = [
  'AFB_Detected',
  'AFB',
  'Acid_Fast_Bacilli',
  'acid-fast bacilli',
  'bacillus',
  'bacilli',
  'leprosy_bacillus',
  'mycobacterium_leprae'
];
const LEPROSY_RUNNER_IDENTIFIERS = [
  'leprosy',
  'lepra',
  'hansen',
  'afb',
  'bacilli'
];
const MOCK_AFB_BACILLI_TERM_ID = 2006475;
export function isLeprosyRunner(runnerName = '') {
  const normalizedName = String(runnerName).toLowerCase();
  return LEPROSY_RUNNER_IDENTIFIERS.some(identifier => normalizedName.includes(identifier));
}

function toFiniteNumber(value, fallback = 0) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function stableMockConfidenceForAnnotation(annotation, index) {
  const existingConfidence = annotation?.confidence ?? annotation?.probability ?? annotation?.score;
  if (existingConfidence !== undefined && existingConfidence !== null) {
    return clampConfidence(existingConfidence);
  }

  const key = String(annotation?.id ?? index + 1);
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) % 10000;
  }

  return Number((0.45 + (hash % 5400) / 10000).toFixed(4));
}

function confidenceThresholdForType(result, type) {
  return confidenceThresholdForTerm({
    result,
    type,
    defaultThreshold: DEFAULT_AFB_CONFIDENCE_THRESHOLD
  });
}

function visibilityByType(section, defaultVisible = true) {
  return visibilityByTerm(LEPROSY_DETECTION_TYPES, section, defaultVisible);
}

function normalizeDetectionType(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (LEPROSY_AFB_TERMS.map(term => term.toLowerCase()).includes(normalized)) {
    return 'afb';
  }
  if (normalized.includes('afb') || normalized.includes('bacill')) {
    return 'afb';
  }
  return normalized;
}

function freezeArray(items = []) {
  return Object.freeze(items);
}

function freezeDetection(detection) {
  if (!detection || Object.isFrozen(detection)) {
    return detection;
  }
  return Object.freeze(detection);
}

function freezeLeprosyDetections(detections = []) {
  if (Object.isFrozen(detections)) {
    return detections;
  }
  return freezeArray((Array.isArray(detections) ? detections : []).map(freezeDetection));
}

function centroidCoordinate(raw, axis) {
  const centroid = raw?.centroid || raw?.location?.centroid;
  return raw?.[axis] ?? raw?.location?.[axis] ?? centroid?.[axis];
}

function normalizeCentroid(raw, x, y) {
  const centroid = raw?.centroid || raw?.location?.centroid;
  const centroidX = centroid?.x ?? x;
  const centroidY = centroid?.y ?? y;
  if (!Number.isFinite(Number(centroidX)) || !Number.isFinite(Number(centroidY))) {
    return null;
  }
  return {
    x: Number(centroidX),
    y: Number(centroidY)
  };
}

function normalizeDetection(raw, index) {
  const type = normalizeDetectionType(raw?.type || raw?.class || raw?.name || raw?.termName || raw?.label);
  const x = toFiniteNumber(centroidCoordinate(raw, 'x'));
  const y = toFiniteNumber(centroidCoordinate(raw, 'y'));
  const centroid = normalizeCentroid(raw, x, y);
  return {
    id: raw?.id || `${type || 'detection'}-${index + 1}`,
    type,
    confidence: Math.max(0, Math.min(1, toFiniteNumber(raw?.confidence))),
    x,
    y,
    centroid,
    location: typeof raw?.location === 'string' ? raw.location : null,
    geometryCompression: raw?.geometryCompression,
    class: raw?.class,
    created: raw?.created,
    updated: raw?.updated,
    user: raw?.user,
    image: raw?.image,
    project: raw?.project,
    slice: raw?.slice,
    term: raw?.term,
    userByTerm: raw?.userByTerm,
    area: raw?.area,
    areaUnit: raw?.areaUnit,
    perimeter: raw?.perimeter,
    perimeterUnit: raw?.perimeterUnit,
    aiVisualizationType: raw?.aiVisualizationType,
    termName: raw?.termName || raw?.name || raw?.label,
    cropURL: raw?.cropURL,
    smallCropURL: raw?.smallCropURL,
    included: raw?.included !== false
  };
}

export function includedLeprosyDetections(result) {
  return (result?.detections || []).filter(detection => (
    detection.included !== false && detection.confidence >= confidenceThresholdForType(result, detection.type)
  ));
}

function buildDetectionSummary(result) {
  const includedByType = {
    afb: []
  };
  const totalByType = {
    afb: 0
  };
  const confidenceSumByType = {
    afb: 0
  };

  (result?.detections || []).forEach(detection => {
    const type = normalizeDetectionType(detection.type);
    if (!LEPROSY_DETECTION_TYPES.includes(type)) {
      return;
    }
    totalByType[type] += 1;
    if (detection.included === false || detection.confidence < confidenceThresholdForType(result, type)) {
      return;
    }
    includedByType[type].push(detection);
    confidenceSumByType[type] += detection.confidence;
  });

  return Object.freeze({
    includedByType: Object.freeze({
      afb: freezeArray(includedByType.afb)
    }),
    sortedIncludedByType: Object.freeze({
      afb: freezeArray(includedByType.afb.slice().sort((a, b) => b.confidence - a.confidence))
    }),
    countByType: Object.freeze({
      afb: includedByType.afb.length
    }),
    totalByType: Object.freeze(totalByType),
    meanConfidenceByType: Object.freeze({
      afb: includedByType.afb.length ? confidenceSumByType.afb / includedByType.afb.length : null
    })
  });
}

function rawAfbCount(data = {}) {
  const candidates = [
    data?.AFB_Detected?.count,
    data?.Total_AFB?.count,
    data?.totalAfb,
    data?.totalAFB,
    data?.totalAfbCount,
    data?.totalBacilli,
    data?.bacilliCount,
    data?.count
  ];
  const candidate = candidates.find(value => value !== undefined && value !== null);
  return toFiniteNumber(candidate, 0);
}

export function leprosyGradingForCount(count) {
  const total = toFiniteNumber(count, 0);
  if (total <= 0) {
    return 'No AFB Seen';
  }
  if (total <= 10) {
    return 'Scanty';
  }
  if (total <= 100) {
    return '1+';
  }
  if (total <= 1000) {
    return '2+';
  }
  return '3+';
}

export function recomputeLeprosyResult(result, options = {}) {
  const includeVisualizations = options.includeVisualizations !== false;
  const fallbackThreshold = clampConfidence(result?.thresholds?.confidence, DEFAULT_AFB_CONFIDENCE_THRESHOLD);
  const existingByTypeThresholds = result.thresholds?.byType || {};
  const thresholds = {
    ...result.thresholds,
    confidence: fallbackThreshold,
    byType: {
      ...existingByTypeThresholds,
      afb: confidenceThresholdForType(result, 'afb')
    }
  };

  const normalizedResult = {
    ...result,
    thresholds
  };
  const detectionSummary = buildDetectionSummary(normalizedResult);
  const includedAfb = detectionSummary.includedByType.afb;
  const hasSyncedDetections = (normalizedResult.detections || []).length > 0;
  const totalAfbCount = hasSyncedDetections
    ? includedAfb.length
    : toFiniteNumber(normalizedResult?.summary?.rawTotalAfbCount);
  const aiConfidence = hasSyncedDetections
    ? detectionSummary.meanConfidenceByType.afb
    : normalizedResult.summary?.aiConfidence;
  const status = totalAfbCount > 0 ? 'Detected' : 'No AFB Seen';
  const grading = leprosyGradingForCount(totalAfbCount);
  const overlayPoints = includeVisualizations
    ? includedAfb.map(detection => ({
      id: detection.id,
      type: detection.type,
      confidence: detection.confidence,
      x: detection.x,
      y: detection.y,
      centroid: detection.centroid,
      location: detection.location,
      geometryCompression: detection.geometryCompression,
      class: detection.class,
      created: detection.created,
      updated: detection.updated,
      user: detection.user,
      image: detection.image,
      project: detection.project,
      slice: detection.slice,
      term: detection.term,
      userByTerm: detection.userByTerm,
      area: detection.area,
      areaUnit: detection.areaUnit,
      perimeter: detection.perimeter,
      perimeterUnit: detection.perimeterUnit,
      aiVisualizationType: detection.aiVisualizationType,
      termName: detection.termName,
      cropURL: detection.cropURL,
      smallCropURL: detection.smallCropURL,
      visible: true
    }))
    : (normalizedResult.overlay?.annotations || normalizedResult.overlay?.points || []);
  const heatmapPoints = includeVisualizations
    ? includedAfb.map(detection => ({
      id: detection.id,
      type: detection.type,
      x: detection.x,
      y: detection.y,
      weight: detection.confidence
    }))
    : (normalizedResult.heatmap?.points || []);

  return {
    ...normalizedResult,
    thresholds,
    summary: {
      ...normalizedResult.summary,
      totalAfbCount,
      aiConfidence,
      status,
      grading
    },
    derived: detectionSummary,
    heatmap: {
      ...normalizedResult.heatmap,
      visible: visibilityForTerm(normalizedResult.heatmap, 'afb', false),
      byTypeVisible: visibilityByType(normalizedResult.heatmap, false),
      points: heatmapPoints
    },
    overlay: {
      ...normalizedResult.overlay,
      visible: visibilityForTerm(normalizedResult.overlay, 'afb', false),
      byTypeVisible: visibilityByType(normalizedResult.overlay, false),
      annotations: overlayPoints,
      points: overlayPoints
    },
    report: {
      ...normalizedResult.report,
      detectionStatus: status,
      finalTotalAfbCount: totalAfbCount,
      finalGrading: grading
    }
  };
}

export function formatLeprosyResults(jobId, runnerName, data = {}, needsResultSync = false) {
  const rawDetections = Array.isArray(data?.detections) ? data.detections : [];
  const hasGlobalThreshold = data?.confidenceThreshold !== undefined || data?.thresholds?.confidence !== undefined;
  const threshold = clampConfidence(data?.confidenceThreshold ?? data?.thresholds?.confidence, DEFAULT_AFB_CONFIDENCE_THRESHOLD);
  const byTypeThresholds = data?.thresholds?.byType || {};
  const rawTotalAfbCount = rawAfbCount(data);
  const detections = freezeLeprosyDetections(
    rawDetections
      .map(normalizeDetection)
      .filter(detection => detection.type === 'afb')
  );

  const result = {
    id: jobId,
    runnerName,
    modelType: LEPROSY_MODEL_TYPE,
    needsResultSync,
    visible: true,
    isLeprosy: true,
    showAllAnnotations: true,
    rawData: data || {},
    detections,
    annotations: [],
    warnings: [],
    thresholds: {
      confidence: threshold,
      byType: {
        afb: clampConfidence(
          byTypeThresholds.afb ?? (hasGlobalThreshold ? threshold : DEFAULT_AFB_CONFIDENCE_THRESHOLD),
          DEFAULT_AFB_CONFIDENCE_THRESHOLD
        )
      }
    },
    sectionCollapsed: {
      summary: false,
      grading: false,
      report: false,
      activity: true,
      printedReport: false
    },
    summary: {
      status: data?.status || 'No AFB Seen',
      totalAfbCount: 0,
      rawTotalAfbCount,
      grading: 'No AFB Seen',
      aiConfidence: toFiniteNumber(data?.aiConfidence ?? data?.confidence, null),
      hotspotImageStatus: data?.hotspotImageStatus || 'Pending'
    },
    heatmap: {
      visible: false,
      byTypeVisible: visibilityByType(null, false),
      opacity: 0.8,
      radius: 18,
      blur: 22,
      points: []
    },
    overlay: {
      visible: false,
      byTypeVisible: visibilityByType(null, false),
      showConfidenceLabelsByType: {
        afb: false
      },
      opacity: 0.9,
      points: []
    },
    report: {
      detectionStatus: data?.status || 'No AFB Seen',
      finalTotalAfbCount: 0,
      finalGrading: 'No AFB Seen',
      verifiedBy: data?.verifiedBy || '',
      dateReported: data?.dateReported || '',
      remarks: data?.remarks || '',
      reviewStatus: data?.reviewStatus || 'Pending'
    },
    activityLog: Array.isArray(data?.activityLog) ? data.activityLog : []
  };

  return recomputeLeprosyResult(result);
}

export function createMockLeprosyPayload() {
  const sourceAnnotations = (Array.isArray(MOCK_LEPROSY_ANNOTATIONS) ? MOCK_LEPROSY_ANNOTATIONS : [])
    .filter(annotation => Array.isArray(annotation?.term) && annotation.term.includes(MOCK_AFB_BACILLI_TERM_ID));
  const detections = sourceAnnotations.map((annotation, index) => {
    const x = toFiniteNumber(centroidCoordinate(annotation, 'x'));
    const y = toFiniteNumber(centroidCoordinate(annotation, 'y'));

    return {
      id: `source-annotation-${annotation?.id ?? index + 1}`,
      sourceAnnotationId: annotation?.id,
      name: 'AFB_Detected',
      sourceName: 'Malaria_Detected',
      aiVisualizationType: 'confidence_heatmap',
      confidence: stableMockConfidenceForAnnotation(annotation, index),
      x,
      y,
      location: typeof annotation?.location === 'string' ? annotation.location : null,
      geometryCompression: annotation?.geometryCompression ?? false,
      centroid: normalizeCentroid(annotation, x, y),
      created: annotation?.created,
      updated: annotation?.updated,
      image: annotation?.image,
      project: annotation?.project,
      slice: annotation?.slice,
      term: annotation?.term,
      area: annotation?.area,
      areaUnit: annotation?.areaUnit,
      perimeter: annotation?.perimeter,
      perimeterUnit: annotation?.perimeterUnit,
      cropURL: annotation?.cropURL,
      smallCropURL: annotation?.smallCropURL,
      imageURL: annotation?.imageURL,
      sourceAnnotation: annotation
    };
  });
  const maxConfidence = detections.length
    ? Math.max(...detections.map(detection => detection.confidence))
    : 0;

  return {
    status: 'Detected',
    sourceModel: 'malaria',
    sourceTerm: 'Malaria_Detected',
    sourceAnnotationList: 'response_1782378366226.json',
    aiConfidence: Number(maxConfidence.toFixed(2)),
    confidenceThreshold: 0,
    thresholds: {
      byType: {
        afb: 0
      }
    },
    AFB_Detected: {
      count: detections.length
    },
    totalBacilli: detections.length,
    reviewStatus: 'Pending',
    detections
  };
}
